import { Link, NavLink } from "@remix-run/react";
import type { LinksFunction } from "@vercel/remix";
import { SignedIn, SignedOut, UserButton, SignUpButton } from "@clerk/remix";
import Footer from "~/components/Footer";
import StructuredData from "~/components/StructuredData";
import BlackFridayBanner from "~/components/BlackFridayBanner";

const scrollToElement = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const faqData = {
  generalQuestions: [
    {
      question: "Why is AWS Certification Important?",
      answer:
        "AWS certification validates your cloud computing expertise and AWS knowledge. It can increase your earning potential and demonstrate your commitment to cloud technology to employers. It can also be a quick way to enhance your resume and LinkedIn profile so you turn up in more search results.",
    },
    {
      question: "What Does an AWS Certification Look Like?",
      answer:
        "An AWS certification includes several components: 1) A digital certificate in PDF format, 2) A digital badge for sharing on LinkedIn and other platforms, and 3) Permission to use the AWS Certified logo. The certification is valid for three years from the date of certification.",
    },
    {
      question: "Are AWS Certification Exams Open Book?",
      answer:
        "No, AWS certification exams are not open book. They are proctored exams (either online or in-person) where you cannot use external resources, notes, or documentation. The exam environment is strictly controlled to maintain certification integrity. This is why thorough preparation using practice exams and study materials is essential.",
    },
    {
      question: "Can AWS Certification Help You Get a Job?",
      answer:
        "It's not going to get you a job on its own but it will help you get noticed by employers and help you craft your professional narrative on your resume and LinkedIn. The Cloud Practitioner certification particularly helps candidates demonstrate fundamental AWS knowledge and commitment to cloud technology.",
    },
    {
      question: "Which AWS Certification Should I Get First?",
      answer:
        "For beginners, the AWS Cloud Practitioner certification is the recommended starting point. It provides a solid foundation in cloud concepts, AWS services, security, architecture, pricing, and support. This certification requires no prior cloud experience and serves as an excellent stepping stone to more advanced AWS certifications.",
    },
    {
      question: "Which AWS Certification is Most Difficult?",
      answer:
        "The AWS Certified Solutions Architect - Professional and AWS Certified DevOps Engineer - Professional are considered the most challenging AWS certifications. Among specialty certifications, the Networking and Machine Learning specialties are particularly demanding. The Cloud Practitioner certification is designed to be the most accessible starting point.",
    },
    {
      question: "Are AWS Certifications free?",
      answer:
        "No, AWS certifications are not free. The AWS Cloud Practitioner certification costs $100 USD.",
    },
    {
      question: "Is AWS Certification hard?",
      answer:
        "The AWS Cloud Practitioner certification is designed to be the most accessible starting point. It requires dedicated preparation and practice but is not overly difficult. The exam tests your knowledge of basic AWS services, cloud concepts, security, pricing, and support. With proper preparation and practice tests, the exam is very manageable for beginners.",
    },
    {
      question:
        "In What Languages is the AWS Cloud Practitioner Exam Available?",
      answer:
        "The AWS Cloud Practitioner exam is available in multiple languages: English, Bahasa (Indonesian), French (France), German, Italian, Japanese, Korean, Portuguese (Brazil), Spanish (Latin America), Spanish (Spain), Simplified Chinese, and Traditional Chinese.",
    },
  ],
  examSpecificQuestions: [
    {
      question: "How Hard is the AWS Cloud Practitioner Exam?",
      answer:
        "The Cloud Practitioner exam is designed to be entry-level, but still requires dedicated preparation. The exam tests your knowledge of basic AWS services, cloud concepts, security, pricing, and support. Most candidates spend 20-30 hours studying over 2-3 months. With proper preparation and practice tests, the exam is very manageable for beginners.",
    },
    {
      question: "What is the Structure of the AWS Cloud Practitioner Exam?",
      answer:
        "The AWS Cloud Practitioner exam (CLF-C02) consists of: 1) 50 scored questions and 15 unscored questions, 2) Multiple choice and multiple response questions, 3) 90 minutes to complete, 4) Questions covering four domains: Cloud Concepts (24%), Security and Compliance (30%), Cloud Technology and Services (34%), and Billing, Pricing, and Support (12%). The exam uses a compensatory scoring model, meaning you only need to pass the overall exam, not each section.",
    },
    {
      question: "What is the Pass Rate for the AWS Cloud Practitioner Exam?",
      answer:
        "While AWS doesn't officially publish pass rates, industry surveys indicate around a 70% pass rate for the Cloud Practitioner exam among well-prepared candidates.",
    },
    {
      question:
        "What Score Do You Need to Pass the AWS Cloud Practitioner Exam?",
      answer:
        "The AWS Cloud Practitioner exam requires a minimum score of 700 out of 1000 points to pass. The exam consists of 65 questions to be completed in 90 minutes. Questions are weighted based on difficulty, and there's no penalty for wrong answers. Fifteen questions are unscored and do not affect your pass/fail outcome.",
    },
    {
      question:
        "How Much Does it Cost to Get AWS Cloud Practitioner Certified?",
      answer: "The AWS Cloud Practitioner exam fee is $100 USD.",
    },
    {
      question: "Can You Retake the AWS Cloud Practitioner Exam?",
      answer:
        "Yes, you can retake the AWS Cloud Practitioner exam if you don't pass. You must wait 14 days between attempts and pay the full exam fee ($100) each time. There's no limit to the number of retakes.",
    },
  ],
  preparationQuestions: [
    {
      question: "How Should I Prepare for the AWS Cloud Practitioner Exam?",
      answer: () => (
        <span>
          Taking practice exams to familiarize yourself with question formats
          question formats and to make sure you understand and retain the
          material is an efficient study plan. If you are not familiar with
          general cloud concepts, watching a{" "}
          <a
            href="https://www.youtube.com/watch?v=NhDYbskXRgc"
            className="text-blue-600 hover:text-blue-700 font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            free training video from freeCodeCamp
          </a>{" "}
          is recommended.
        </span>
      ),
    },
    {
      question: "Is the AWS Cloud Practitioner Exam Online or Offline?",
      answer:
        "The AWS Cloud Practitioner exam can be taken either online (proctored from home) or at a physical testing center. Online exams require a quiet room, stable internet connection, and webcam for proctoring. Physical testing centers provide a controlled environment but may require travel. Both formats deliver the same exam content and scoring.",
    },
    {
      question: "How Long is the AWS Cloud Practitioner Exam?",
      answer: "The AWS Cloud Practitioner exam is 90 minutes long.",
    },
    {
      question: "How Many Questions are on the AWS Cloud Practitioner Exam?",
      answer: "There are 65 questions on the AWS Cloud Practitioner exam.",
    },
  ],
  comparisonQuestions: [
    {
      question: "AWS Certification or Azure Certification?",
      answer:
        "The choice between AWS and Azure certification depends on several factors: 1) Market demand (AWS has larger market share), 2) Your career goals, 3) Your company's cloud provider, and 4) Regional job market preferences. Many professionals get both certifications, starting with AWS Cloud Practitioner due to AWS's market leadership and extensive resources.",
    },
    {
      question:
        "How Does AWS Cloud Practitioner Compare to Associate Level Certifications?",
      answer:
        "The Cloud Practitioner is a foundational certification, while Associate-level certifications (Solutions Architect, Developer, SysOps Administrator) are more technical and specialized. Cloud Practitioner covers broad concepts and basic services, while Associate certifications require deep technical knowledge and hands-on experience with AWS services.",
    },
    {
      question:
        "AWS Cloud Practitioner vs. Google Cloud Digital Leader Certification?",
      answer:
        "Both are entry-level cloud certifications but have key differences: 1) AWS Cloud Practitioner focuses more on technical concepts and service categories, 2) Google Cloud Digital Leader emphasizes business value and digital transformation, 3) AWS exam has more questions (65 vs 50) and longer duration (90 vs 60 minutes), and 4) AWS certification typically has broader job market recognition.",
    },
    {
      question:
        "How Does AWS Cloud Practitioner Compare to IBM Cloud Essentials?",
      answer:
        "These foundational certifications differ in several ways: 1) AWS Cloud Practitioner is a proctored exam while IBM Cloud Essentials is self-paced, 2) AWS certification has stronger market recognition, 3) IBM's certification is free but AWS costs $100, and 4) AWS exam covers more comprehensive cloud concepts. AWS certification typically provides better career advancement opportunities due to AWS's larger market share.",
    },
    {
      question:
        "AWS Cloud Practitioner vs. Oracle Cloud Infrastructure Foundations?",
      answer:
        "Both certifications cover cloud basics but have distinct characteristics: 1) AWS has broader industry acceptance and recognition, 2) Oracle's exam is cheaper ($95) and shorter (85 minutes), 3) Oracle focuses more on infrastructure concepts while AWS covers a broader range of services, 4) AWS certification has more study resources available, 5) Oracle's certification is more relevant for organizations using Oracle Cloud Infrastructure.",
    },
    {
      question: "How Does AWS Cloud Practitioner Compare to CompTIA Cloud+?",
      answer:
        "These certifications serve different purposes: 1) CompTIA Cloud+ is vendor-neutral while AWS is vendor-specific, 2) Cloud+ is more technical and requires prior experience, 3) CompTIA's exam is more expensive ($338) and longer (90 minutes), 4) AWS certification is better for cloud-specific roles, while CompTIA is better for general IT positions, 5) AWS has more frequent updates to match their service offerings.",
    },
    {
      question:
        "AWS Cloud Practitioner vs. Salesforce Certified Platform Basics?",
      answer:
        "While both are cloud certifications, they have different focuses: 1) AWS certification has broader applicability in the job market, 2) Salesforce certification is free but has limited scope, 3) AWS covers infrastructure, while Salesforce emphasizes CRM and business applications, 4) AWS certification provides a foundation for a broader range of cloud careers.",
    },
  ],
};

const allQuestions = [
  ...faqData.generalQuestions,
  ...faqData.examSpecificQuestions,
  ...faqData.preparationQuestions,
  ...faqData.comparisonQuestions,
];

// Create the FAQ Schema structure
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: allQuestions.map((qa) => ({
    "@type": "Question",
    name: qa.question,
    acceptedAnswer: {
      "@type": "Answer",
      text:
        typeof qa.answer === "function"
          ? "Taking practice exams to familiarize yourself with question formats and to make sure you understand and retain the material is an efficient study plan. If you are not familiar with general cloud concepts, watching a free training video from freeCodeCamp is recommended."
          : qa.answer,
    },
  })),
};

// Breadcrumb Schema
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://cloudexamhub.com",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "AWS Certification FAQ",
      item: "https://cloudexamhub.com/certification-faqs",
    },
  ],
};

export const links: LinksFunction = () => [
  { rel: "canonical", href: "https://cloudexamhub.com/certification-faqs" },
];

// Add metadata export for Remix
export function meta() {
  return [
    { title: "AWS Cloud Practitioner Certification FAQs 2026" },
    {
      name: "description",
      content:
        "Comprehensive AWS Cloud Practitioner certification FAQs. Learn about exam structure, costs, preparation tips, and compare with other cloud certifications. Updated for 2026.",
    },
    {
      name: "keywords",
      content:
        "AWS Cloud Practitioner, AWS certification, CLF-C02, cloud certification, AWS exam, cloud computing certification",
    },
    { property: "og:title", content: "AWS Cloud Practitioner Certification FAQs 2026" },
    {
      property: "og:description",
      content:
        "Comprehensive AWS Cloud Practitioner certification FAQs. Exam structure, costs, preparation tips, and certification comparisons. Updated for 2026.",
    },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://cloudexamhub.com/certification-faqs" },
    { property: "og:image", content: "https://cloudexamhub.com/cloud-exam-hub-full-logo-white-background.png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: "https://cloudexamhub.com/cloud-exam-hub-full-logo-white-background.png" },
    { name: "twitter:title", content: "AWS Cloud Practitioner Certification FAQs 2026" },
    {
      name: "twitter:description",
      content:
        "Comprehensive AWS Cloud Practitioner certification FAQs. Exam structure, costs, preparation tips, and certification comparisons.",
    },
  ];
}

export default function AwsCertificationFaq() {
  return (
    <div className="flex flex-col min-h-screen mx-auto">
      <StructuredData data={faqSchema} />
      <StructuredData data={breadcrumbSchema} />
      <BlackFridayBanner />
      <header
        role="banner"
        className="sticky top-0 z-50 bg-white border-b px-4 sm:px-8 lg:px-12 h-16 sm:h-14 flex items-center"
      >
        <NavLink to="/" className="flex items-center justify-center">
          <img
            src="/cloud-check.png"
            alt="Cloud Exam Hub"
            className="h-8 w-8 sm:h-6 sm:w-6"
          />
          <span className="ml-2 font-bold text-lg sm:text-xl truncate">
            Cloud Exam Hub
          </span>
        </NavLink>
        <nav className="ml-auto flex gap-4 sm:gap-4 items-center">
          <div className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-white text-blue-600 hover:bg-gray-100 h-10 px-4 py-2">
            <SignedIn>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
            <SignedOut>
              <SignUpButton mode="modal">Sign Up</SignUpButton>
            </SignedOut>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section
        aria-label="Page Header"
        className="w-full py-20 md:py-28 lg:py-32 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center text-white">
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl mb-6">
              AWS Cloud Practitioner FAQs 2026
            </h1>
            <p className="max-w-[800px] mx-auto text-gray-200 text-lg md:text-xl lg:text-2xl">
              Everything You Need to Know About AWS Cloud Practitioner
              Certification
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <nav
        aria-label="Table of Contents"
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-7xl"
      >
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-center mb-8">
          Quick Navigation
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { id: "general", text: "General AWS Certification Questions" },
            { id: "exam-specific", text: "Cloud Practitioner Exam Details" },
            { id: "preparation", text: "Exam Preparation Tips" },
            { id: "comparison", text: "Certification Comparisons" },
          ].map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToElement(link.id)}
              className="flex items-center justify-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-blue-600 hover:text-blue-700 text-center font-medium cursor-pointer"
            >
              {link.text}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <article>
          {/* General Questions Section */}
          <section id="general" className="py-8 md:py-12">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-8">
              General AWS Certification Questions
            </h2>
            <div className="grid gap-4 lg:gap-6">
              {faqData.generalQuestions.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    {faq.question}
                  </h3>
                  <div className="prose max-w-none text-gray-600 mb-4">
                    {faq.answer}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Exam Specific Questions Section */}
          <section id="exam-specific" className="py-8 md:py-12 bg-gray-50">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-8">
              Cloud Practitioner Exam Details
            </h2>
            <div className="grid gap-4 lg:gap-6">
              {faqData.examSpecificQuestions.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    {faq.question}
                  </h3>
                  <div className="prose max-w-none text-gray-600 mb-4">
                    {faq.answer}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Preparation Questions Section */}
          <section id="preparation" className="py-8 md:py-12">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-8">
              Exam Preparation Tips
            </h2>
            <div className="grid gap-4 lg:gap-6">
              {faqData.preparationQuestions.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    {faq.question}
                  </h3>
                  <div className="prose max-w-none text-gray-600 mb-4">
                    {typeof faq.answer === "function"
                      ? faq.answer()
                      : faq.answer}
                  </div>
                </div>
              ))}
            </div>

            {/* Contextual CTA after preparation tips */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
              <p className="text-lg font-semibold text-blue-900 mb-2">
                Ready to put your prep into practice?
              </p>
              <p className="text-gray-600 mb-6">
                Cloud Exam Hub has 500+ up-to-date CLF-C02 practice questions with detailed explanations — the fastest way to find and fix your weak spots.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <SignedOut>
                  <SignUpButton mode="modal">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-lg text-lg transition-colors">
                      Start Practicing Free →
                    </button>
                  </SignUpButton>
                </SignedOut>
                <SignedIn>
                  <Link
                    to="/user-home"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-lg text-lg transition-colors"
                  >
                    Go to Practice Exams →
                  </Link>
                </SignedIn>
                <p className="text-sm text-gray-500">$11.99 one-time · 14-day money-back guarantee</p>
              </div>
            </div>
          </section>

          {/* Comparison Questions Section */}
          <section id="comparison" className="py-8 md:py-12 bg-gray-50">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-8">
              Certification Comparisons
            </h2>
            <div className="grid gap-4 lg:gap-6">
              {faqData.comparisonQuestions.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    {faq.question}
                  </h3>
                  <div className="prose max-w-none text-gray-600 mb-4">
                    {faq.answer}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </article>

        {/* Related Content Section */}
        <aside aria-label="Related Resources" className="py-8 md:py-12">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">Related Resources</h2>
            <nav aria-label="Related Links">
              <ul className="space-y-4">
                <li>
                  <Link
                    to="/"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    AWS Cloud Practitioner Practice Exams — Cloud Exam Hub
                  </Link>
                </li>
                <li>
                  <Link
                    to="/checklist"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    AWS Cloud Practitioner 4-Week Study Checklist
                  </Link>
                </li>
                <li>
                  <Link
                    to="/glossary"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    AWS Cloud Practitioner Glossary of Key Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://d1.awsstatic.com/training-and-certification/docs-cloud-practitioner/AWS-Certified-Cloud-Practitioner_Exam-Guide.pdf"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    AWS Certified Cloud Practitioner (CLF-C02) Exam Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://www.youtube.com/watch?v=NhDYbskXRgc"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Free AWS Cloud Practitioner Training Video (freeCodeCamp)
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </aside>
      </main>

      {/* Bottom Conversion Section */}
      <section className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400 py-16 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Pass Your Exam?</h2>
          <p className="text-lg text-blue-100 mb-8">
            Stop reading about the exam — start practicing for it. Cloud Exam Hub gives you 500+ CLF-C02 practice questions with detailed answer explanations, unlimited exam simulations, and a performance dashboard to track your weak spots.
          </p>
          <div className="bg-white/10 rounded-xl p-6 mb-8 grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold">500+</div>
              <div className="text-blue-100">Practice questions</div>
            </div>
            <div>
              <div className="text-2xl font-bold">Unlimited</div>
              <div className="text-blue-100">Exam simulations</div>
            </div>
            <div>
              <div className="text-2xl font-bold">14-day</div>
              <div className="text-blue-100">Money-back guarantee</div>
            </div>
          </div>
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-lg text-xl transition-colors shadow-lg">
                Get Access for $11.99 →
              </button>
            </SignUpButton>
            <p className="mt-3 text-blue-200 text-sm">One-time payment · No subscription · Was $19.99</p>
          </SignedOut>
          <SignedIn>
            <Link
              to="/user-home"
              className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-lg text-xl transition-colors shadow-lg inline-block"
            >
              Go to Practice Exams →
            </Link>
          </SignedIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
